import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import _ from 'lodash'

import './styles.scss'
import { apiInchat } from 'api'
import inchatActions from 'redux/inchat/actions'
import ActionsBar from '../ActionsBar'
import HtmlEditor from 'components/HtmlEditor'
import { isDisabledSend, isMessageEmpty } from 'components/Chat/ChatChannels/ChatComposer/utils/validations'
import Nixi1Spinner from 'components/Nixi1Spinner'
import FilePreview from 'components/UploadFiles/FilePreview'
import ScheduleMsgModal from 'components/Chat/ScheduleMsgModal'
import { lang } from 'utils/helpers'
import { toastMessage } from 'utils/messages'
import MenuMentions from './MenuMentions'
import { detectLastWord, removeHTML, setFormatEditorMentions, verifyIdIncomplete } from '../utils'
import { DateTime } from 'luxon'
import { getScheduledDate } from './utils'
import { store } from '../../../redux'

const Composer = props => {
  const { conversationId, channel, isThread, composer,  parentId, sendMessageAlt, scheduled } = props
  const dispatch = useDispatch()
  const [refEditor, setRefEditor] = useState(null)
  const [isOpenScheduleMsgModal, setIsOpenScheduleMsgModal] = useState(false)
  const [isSchedulingMessage, setIsSchedulingMessage] = useState(false)
  const [schedulingMessageError, setSchedulingMessageError] = useState('')
  const [isOpenMenuMentions, setIsOpenMenuMentions] = useState(false)
  const newConversationId = parentId ? `${conversationId}_${parentId}` : `${conversationId}` // ONLY FOR THREADS

  const [operators, setOperators] = useState([])

  const disabledSend = isDisabledSend({
    message: composer?.message,
    isLoading: composer?.uploading || composer?.sending,
    attachments: composer?.attachments,
  })

  useEffect(() => {
    getDrafts()
    return () => {
      inChatDraft()
    }
  }, [])

  const debouncedSearchOperators = useRef(
    _.debounce(text => {
      getUsersSearch(text)
    }, 350)
  ).current

  useEffect(() => {
    if (composer?.message === '') {
      updateEditor('')
    } else{
      if(composer?.external){
        updateEditor(composer.message)
        dispatch(inchatActions.updateMessageExternal(false))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composer?.message])

  const getDrafts = async () => {
    dispatch(inchatActions.getDraftsByConversation(conversationId, parentId))
  }

  const getUsersSearch = async (text = '') => {
    try {
      const { data } = await apiInchat.searchUsers({
        text,
        pageSize: 100,
      })
      setOperators(data.users)
    } catch (error) {
      toastMessage(lang('dashboard_error_get_operators'), 'error')
    }
  }

  const toggleScheduleMsgModal = () => {
    if (schedulingMessageError) setSchedulingMessageError('')
    setIsOpenScheduleMsgModal(prevState => !prevState)
  }

  const verifyId = text => {
    const textClean = removeHTML(text)
    const { isShow, lastWord } = detectLastWord(textClean)
    setIsOpenMenuMentions(isShow)
    if (isShow) {
      debouncedSearchOperators(lastWord.substring(1))
    } else {
      setOperators([])
    }
  }

  const onWriteMessage = ({ target }) => {
    const text = target.value === '<p><br></p>' ? '' : verifyIdIncomplete(target.value, composer?.mentions)
    dispatch(inchatActions.editMessage({
      message: text,
      isThread,
      messageId: composer.messageId,
      external: true,
      parentId: parentId,
      conversationId: newConversationId,
    }))
    verifyId(target.value)
  }

  const onKeyUp = (e) => {
    // if (e.shiftKey && e.key === 'Enter') {
    //   !disabledSend && sendMessageHotKey(e)
    // }
    // console.log('onKeyUp', refEditor);
  }

  const updateEditor = useCallback(
    (value = '') => {
      if (refEditor?.__componentStatus === 'ready') {
        const isEmpty = isMessageEmpty(refEditor?.getEditorValue())
        if (!value && isEmpty) return
        refEditor.setEditorValue(value)
      }
    }, [refEditor]
  )

  const handleScheduleMessage = ({ dateType, date }) => {
    if (schedulingMessageError) setSchedulingMessageError('')
    const message = composer.message || ''
    if (!message) {
      setSchedulingMessageError(lang('dashboard_message-text-empty'))
      return
    }

    let isoDate = dateType === 'tomorrow-0900'
      ? getScheduledDate('tomorrow-0900')
      : dateType === 'monday-0900'
        ? getScheduledDate('monday-0900')
        : date

    const data = {
      conversationId,
      message: message.replace( /(<([^>]+)>)/ig, ''),
      scheduledAt: isoDate
    }
    if (composer.attachments?.length) data.attachments = composer.attachments

    setIsSchedulingMessage(true)
    const scheduledMessage = scheduled.data.find(msg => msg._id === scheduled.messageToEdit?.id)
    if (scheduledMessage) {
      apiInchat.updateScheduledMessage({ id: scheduled.messageToEdit?.id, data })
        .then(() => {
          toastMessage(lang('dashboard_message-scheduled-successfully'))
          if (isOpenScheduleMsgModal) toggleScheduleMsgModal()
          dispatch(inchatActions.cleanComposer(conversationId))
          dispatch(inchatActions.getScheduledMessages())
        })
        .catch(() => toastMessage(lang('dashboard_message-scheduled-error'), 'error'))
        .finally(() => setIsSchedulingMessage(false))
    } else {
      apiInchat.scheduleMessage(data)
        .then(() => {
          toastMessage(lang('dashboard_message-scheduled-successfully'))
          if (isOpenScheduleMsgModal) toggleScheduleMsgModal()
          dispatch(inchatActions.cleanComposer(conversationId))
          dispatch(inchatActions.getScheduledMessages())
        })
        .catch(() => toastMessage(lang('dashboard_message-scheduled-error'), 'error'))
        .finally(() => setIsSchedulingMessage(false))
    }
  }

  const handlePressOperator = (value) => {
    let newText = composer.message === '<p><br></p>' ? '' :composer.message
    const atIndex = composer.message.lastIndexOf('@')
    if(atIndex >= 0){
      newText = newText.slice(0, atIndex + 1) + value[value.length - 1]._id
    }
    dispatch(inchatActions.editMessage({
      message: setFormatEditorMentions(newText, value),
      isThread,
      messageId: composer.messageId,
      external: true,
      parentId: parentId,
      conversationId: newConversationId,
    }
    ))
    dispatch(inchatActions.updateMentions(newConversationId, value, isThread))
  }

  const sendMessage = () => {
    if (sendMessageAlt) {
      sendMessageAlt(conversationId, isThread, newConversationId)
      return
    }
    dispatch(inchatActions.sendMessage(conversationId, isThread, null, newConversationId))
  }

  const inChatDraft = () => {
    // GET CONVERSAION FROM REDUX DIRECTLY
    const conversationPrincipal = store.getState().inchat.conversations[conversationId]
    const _conversation = store.getState().inchat.conversations[newConversationId]

    // GET COMPOSER FROM REDUX
    const _composer = _conversation?.composer || {}

    if (_composer?.message || _composer?.attachments?.length > 0) {
      const messageToSave = {
        content: _composer.message,
        contentHtml: _composer.message,
        parentId: parentId || undefined,
        conversationId: conversationId,
        userIds: conversationPrincipal.conversation.users,
        attachments: _composer.attachments?.length > 0 ? _composer.attachments : undefined,
      }
      if (_composer.draftId) {
        apiInchat.draftUpdate({ ...messageToSave, draftId: _composer.draftId })
          .then(response => {
            cleanAndRemove()
          })
          .catch(() => toastMessage(lang('inchat_error_draft_not_save'), 'error'))
      } else {
        apiInchat.draftAdd(messageToSave)
          .then(response => {
            cleanAndRemove()
          })
          .catch(() => toastMessage(lang('inchat_error_draft_not_save'), 'error'))
      }
    } else {
      cleanAndRemove()
    }
  }

  const cleanAndRemove = () => {
    dispatch(inchatActions.cleanComposer(newConversationId, isThread))
    dispatch(inchatActions.removeConversation(newConversationId)) //REQUIRE THE CONVERSATIONID WITH THE PARENTID
  }

  return (
    <div className='container-composer'>
      <div className='composer'>
        <HtmlEditor
          key={`editor-${newConversationId}`}
          onChange={value => onWriteMessage({ target: { value: value || '' } })}
          defaultValue={composer.message ? _.replace(composer.message, /\n/g, '<br>') : null}
          placeholder={lang('dashboard_write-message')}
          onKeyUp={(e)=>onKeyUp(e)}
          setInstance={(instance) => setRefEditor(instance)}
          channel={channel}
          minHeight={70}
          maxHeight={500}
          enter='P'//SI SE CAMBIA A BR, PRESENTA EL BUG DEL ENTER QUE MANDA EL CURSOR AL INICIO DE LA LINEA
          buttonCloseEvent={undefined}
        />

        {isOpenMenuMentions && operators.length ?
          <div className='composer__mentions'>
            <MenuMentions
              operators={operators}
              handlePressOperator={handlePressOperator}
              operatorsSelected={composer?.mentions || []}
            />
          </div>
          : null
        }

        <div className='uploaded-files'>
          {composer?.uploading && <Nixi1Spinner width='25px' height='25px' />}
          {/* {filesErrorMessage && <p className='files-error'>{I18n.t(filesErrorMessage)}</p>} */}
          {!composer?.uploading && composer?.attachments?.length > 0 && (
            <div className='chat-composer__uploaded-files-preview'>
              {composer.attachments.map((file, key) => (
                <FilePreview
                  key={file.url + key}
                  iconCleanedClose
                  id={file.id}
                  url={file.urlLong || file.url}
                  thumb={file.thumb}
                  shortUrl={file.url || file.urlLong}
                  type={file.mime}
                  mimeType={file.mimeType}
                  mimeSubtype={file.mimeSubtype}
                  extension={file.extension}
                  onRemoveFile={(attachmentId) => dispatch(inchatActions.editMessageRemoveAttachment(newConversationId, attachmentId, isThread))}
                />
              ))}
            </div>
          )}
        </div>

        <div className='actions'>
          <ActionsBar
            composer={composer}
            conversationId={conversationId}
            newConversationId={newConversationId}
            disabledSend={disabledSend}
            onSendMessage={sendMessage}
            channel={channel}
            updateEditor={updateEditor}
            refEditor={refEditor}
            isThread={isThread}
            toggleScheduleMsgModal={toggleScheduleMsgModal}
            handleScheduleMessage={handleScheduleMessage}
          />
        </div>
      </div>

      {/* Schedule Msg Modal */}
      {isOpenScheduleMsgModal ? (
        <ScheduleMsgModal
          isOpen={isOpenScheduleMsgModal}
          toggle={toggleScheduleMsgModal}
          defaultValue={scheduled.messageToEdit?.date ? {
            date: DateTime.fromISO(scheduled.messageToEdit.date).toFormat('yyyy-LL-dd'),
            time: DateTime.fromISO(scheduled.messageToEdit.date).toFormat('HH:mm:ss')
          } : null}
          onSchedule={({ iso }) => handleScheduleMessage({ date: iso })}
          isSubmitting={isSchedulingMessage}
          errorText={schedulingMessageError}
        />
      ) : null}
    </div>
  )
}

export default Composer
